import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { LineRow } from "./LineRow";
import { dropTarget } from "./lineDrag";
import { formatMoney } from "../../lib/money";
import type { DraftSection } from "./useQuoteDraft";
import type { LineKind, Money, QuoteLine } from "../../api/types";

// 明細表。表頭隨版面切換,橫向溢位由外層容器吸收,body 不得橫捲。
//
// 最後一欄的表頭是「操作」,底下每一列只有一顆 ⋮ ——
// 插入 / 排序 / 複製 / 刪除都在那個選單裡,詳見 LineRow 與 RowMenu。
//
// 刪除之後的焦點也由這裡接手。刪掉的那一列連同它的 ⋮ 一起卸載,沒有人接手的話
// 焦點會掉回 <body> —— 鍵盤使用者在 45 列的明細表中段刪掉一列,下一次按 Tab
// 就從整份文件最上面重來,完全失去位置感。

const STANDARD_HEADERS = ["序", "摘要", "規格", "單位", "數量", "單價", "金額", "操作"];
const MANPOWER_HEADERS = ["序", "服務項目", "備註", "日期", "時間", "人數", "天數", "單價", "金額", "操作"];

// 整筆包價的章節(業主 2026-09-17:「我們還是需要細節 只是差在 沒有逐一給項目單價」)。
//
// **單價與金額兩欄整個拿掉**,不是留著不填:一欄數字不影響任何合計,
// 遲早有人填了發現小計沒變,以為系統壞了。
// 原本填過的值留在資料裡,切回逐項計價就回來。
const LUMP_STANDARD_HEADERS = ["序", "摘要", "規格", "單位", "數量", "操作"];
const LUMP_MANPOWER_HEADERS = ["序", "服務項目", "備註", "日期", "時間", "人數", "天數", "操作"];

export interface LineTableProps {
  section: DraftSection;
  /** 這個章節的小計。印在 tfoot,與 PDF、XLSX 一樣落在明細的最下面。 */
  subtotal: Money;
  onChangeLine: (lineIndex: number, patch: Partial<QuoteLine>) => void;
  onMoveLine: (from: number, to: number) => void;
  onRemoveLine: (lineIndex: number) => void;
  /** 在指定列的下方插入一列。 */
  onInsertLine: (afterIndex: number, patch: Partial<QuoteLine>) => void;
  /** 在指定列的下方插入一份一模一樣的。 */
  onDuplicateLine: (lineIndex: number) => void;
  /** 章節被刪空時的焦點去處(「新增一行」按鈕)。 */
  emptyFocusRef?: RefObject<HTMLElement | null>;
}

/** 插入或轉成標題列 / 說明列時要一併清掉數量與單價,免得帶著上一個身分的數字進來。 */
const NON_ITEM_PATCH: Partial<QuoteLine> = {
  qty: null,
  headcount: null,
  days: null,
  unit_price: "0",
  amount: "0.00",
};

/**
 * 轉回明細列時要補回的欄位。
 *
 * 標題列與說明列連數量、單價欄都不存在,轉回來時 qty 還是 null 的話,
 * 使用者會看到一個空白的數量欄而不知道該不該填。值與新增一行的預設一致
 * (見 useQuoteDraft 的 LINE_DEFAULTS)。
 */
const ITEM_PATCH: Partial<QuoteLine> = {
  qty: "1",
  unit_price: "0",
  amount: "0.00",
};

/** 切換列型別要送出的完整修補。文字欄一律留著 —— 打好的字不該因為改型別重打。 */
export function kindPatch(kind: LineKind): Partial<QuoteLine> {
  return kind === "ITEM" ? { kind, ...ITEM_PATCH } : { kind, ...NON_ITEM_PATCH };
}

export function LineTable({
  section,
  subtotal,
  onChangeLine,
  onMoveLine,
  onRemoveLine,
  onInsertLine,
  onDuplicateLine,
  emptyFocusRef,
}: LineTableProps) {
  // 這個章節逐項標價嗎。整筆包價的不標 —— 見檔頭那一段。
  const priced = section.mode !== "LUMP_SUM";
  const manpower = section.line_layout === "MANPOWER";
  const headers = priced
    ? manpower
      ? MANPOWER_HEADERS
      : STANDARD_HEADERS
    : manpower
      ? LUMP_MANPOWER_HEADERS
      : LUMP_STANDARD_HEADERS;
  const title = section.title || "(未命名章節)";

  // 刪除之後要把焦點交給哪一列(以 uid 指名)。null = 沒有相鄰列了。
  const focusAfterRemoval = useRef<string | null>(null);
  // 同一個 uid 可能連續被指名兩次,所以用一個只增不減的計數觸發 effect。
  const [removalTick, setRemovalTick] = useState(0);

  // 用 layout effect:要在瀏覽器畫下一格之前就把焦點放好,焦點環才不會閃一下。
  useLayoutEffect(() => {
    if (removalTick === 0) return;
    const uid = focusAfterRemoval.current;
    focusAfterRemoval.current = null;
    // getElementById 而不是 querySelector:uid 不必為了 CSS 選擇器再跳脫一次。
    const row = uid === null ? null : document.getElementById(`nq-line-${uid}`);
    const next = row?.querySelector<HTMLElement>('[data-row-menu-trigger="true"]') ?? null;
    (next ?? emptyFocusRef?.current ?? null)?.focus();
  }, [removalTick, emptyFocusRef]);

  const removeLine = (index: number) => {
    // 遞補到這個位置的是下一列;它是最後一列的話就退回前一列。
    const neighbour = section.lines[index + 1] ?? section.lines[index - 1] ?? null;
    focusAfterRemoval.current = neighbour?.uid ?? null;
    onRemoveLine(index);
    setRemovalTick((tick) => tick + 1);
  };

  // 拖曳排序(業主 2026-09-23:「明細列的上移下移 可以用拖曳的嗎」)。
  //
  // **DndContext 掛在這裡 = 一個章節一個**,所以拖不出這一章 —— 拖到別章的
  // 卡片上會彈回原位。與「上移 / 下移」的範圍一致。
  //
  // distance: 4 —— 少了它,單純點一下序號那一格也會被當成拖曳的開始,
  // 而那一格就在數量欄旁邊,誤觸的代價是整列被搬走。
  //
  // **只給 PointerSensor。** 鍵盤那條路走 ⋮ →「上移 / 下移」,理由見
  // `LineRow.tsx` 的 handleCell 與 `RowMenu.tsx` 開頭。
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const uids = section.lines.map((one) => one.uid);

  const handleDragEnd = (event: DragEndEvent) => {
    const move = dropTarget(uids, event.active?.id, event.over?.id);
    // 什麼都不該做的情況(拖到空處、原地放開、不是這一章的列)全部由
    // dropTarget 判掉;這裡只管搬,而且走的是與「上移」同一支。
    if (move) onMoveLine(move.from, move.to);
  };

  return (
    <div className="w-full overflow-x-auto">
      <table aria-label={`「${title}」明細`} className="w-full min-w-[52rem] border-collapse text-left">
        <thead>
          <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
            {headers.map((header) => (
              <th key={header} scope="col" className="px-2 py-1.5">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        {/* DndContext 與 SortableContext 都不畫任何 DOM,夾在 <tbody> 裡面
            不會弄壞表格結構。 */}
        <tbody>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={uids} strategy={verticalListSortingStrategy}>
              {section.lines.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="px-2 py-3 text-sm text-[var(--text-3)]">
                這個章節還沒有明細。
              </td>
            </tr>
          ) : (
            section.lines.map((line, index) => (
              <LineRow
                key={line.uid}
                line={line}
                rowNo={index + 1}
                layout={section.line_layout}
                priced={priced}
                columnCount={headers.length}
                isFirst={index === 0}
                isLast={index === section.lines.length - 1}
                onChange={(patch) => onChangeLine(index, patch)}
                onMoveUp={() => onMoveLine(index, index - 1)}
                onMoveDown={() => onMoveLine(index, index + 1)}
                onRemove={() => removeLine(index)}
                onInsertSubheading={() => onInsertLine(index, { kind: "SUBHEADING", ...NON_ITEM_PATCH })}
                onInsertNote={() => onInsertLine(index, { kind: "NOTE", ...NON_ITEM_PATCH })}
                onInsertItem={() => onInsertLine(index, { kind: "ITEM", ...ITEM_PATCH })}
                onConvert={(kind) => onChangeLine(index, kindPatch(kind))}
                onDuplicate={() => onDuplicateLine(index)}
              />
            ))
              )}
            </SortableContext>
          </DndContext>
        </tbody>
        {/* 小計在明細的最下面。業主 2026-09-13:「每個項目的小計 請放在 那一個分類的
            最後下面 而不是現在的TOP」。XLSX 的每張明細分頁、PDF 的每個章節,合計
            本來就寫在明細列的下方 —— 只有畫面跑到了標題列,移下來之後三邊讀法一致。

            標籤放在金額左邊那一欄、數字落在「金額」欄,和 xlsx.py 的
            `label_col = amount_col - 1` 是同一個排法。上緣加粗一條分隔線,
            一眼看得出這是小計而不是又一列明細。 */}
        <tfoot>
          <tr className="border-t-2 border-[var(--border-strong)] bg-[var(--surface-2)]">
            <td
              colSpan={Math.max(1, headers.length - 2)}
              className="px-2 py-1.5 text-right text-sm font-medium text-[var(--text-2)]"
            >
              {/* 整筆包價那一欄的數字**不是上面加起來的** —— 字樣要說得出來,
                  不然看的人會拿上面那幾列去驗算,然後來問為什麼加不出來。 */}
              {priced ? "小計" : "整筆包價"}
            </td>
            <td className="w-32 px-2 py-1.5 text-right">
              <span className="tabular text-base font-semibold text-[var(--text)]">{formatMoney(subtotal)}</span>
            </td>
            <td className="w-10 px-2 py-1.5" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
